import {
    Document,
    Packer,
    Paragraph,
    Table,
    TableRow,
    TableCell,
    TextRun,
    ImageRun,
} from 'docx';
import { saveAs } from 'file-saver';
import {IArtifactFlat} from "./export-flat";
import { _get } from 'core/api/_utils/api';


interface ExportDocxOptions {
    fileName?: string;
}

export async function exportArtifactDocx(
    artifact: IArtifactFlat,
    options: ExportDocxOptions = {}
): Promise<void> {
    const { fileName = `${artifact.title}.docx` } = options;

    // 1) Build a new Document
    const doc = await generateDocx(artifact);

    // 2) Pack to a Blob and trigger download
    const blob = await Packer.toBlob(doc);
    saveAs(blob, fileName);
}

export async function generateDocx(artifact: IArtifactFlat): Promise<any> {
    const TARGET_WIDTH = 400;

    // Helper to get image dimensions from base64
    async function getImageDimensionsFromBase64(base64: string): Promise<{ width: number, height: number }> {
        return new Promise((resolve, reject) => {
            const img = new window.Image();
            img.onload = () => resolve({ width: img.width, height: img.height });
            img.onerror = reject;
            img.src = `data:image/png;base64, ${base64}`;
        });
    }

    // Fetch images and get their dimensions
    const imageData = await Promise.all(
        artifact.photos.map(async (id) => {
            const photoUrl = `/Photo/GetBase64/${id}`;
            try {
                const res = await _get(photoUrl); 
                if (res && res.data?.base64) {
                    const { base64 } = res.data;
                    const buf = base64ToArrayBuffer(base64);
                    const { width, height } = await getImageDimensionsFromBase64(base64);
                    const scale = TARGET_WIDTH / width;
                    return {
                        buf,
                        width: TARGET_WIDTH,
                        height: Math.round(height * scale),
                    };
                }
            } catch (err) {
                console.error(err);
            }
            return null;
        })
    );

    // Create image paragraphs
    const imageParagraphs = imageData
        .filter((img): img is { buf: ArrayBuffer, width: number, height: number } => !!img)
        .map((img) =>
            new Paragraph({
                children: [
                    new ImageRun({
                        data: img.buf,
                        type: 'png',
                        transformation: { width: 300, height: 200 }, // Fixed larger size
                    }),
                ],
                spacing: { after: 200 }, // Add some spacing between images
            })
        );

    return new Document({
        sections: [
            {
                properties: {},
                children: [
                    // Id
                    new Paragraph({
                        children: [new TextRun(`artifact_id: ${artifact.id}`)],
                    }),
                    // Images as paragraphs
                    ...imageParagraphs,
                    // Table: "Field" / "Value"
                    new Table({
                        width: {
                            size: 100,
                            type: "pct"
                        },
                        columnWidths: [4000, 8000],
                        rows: [
                            // header row
                            new TableRow({
                                children: [
                                    new TableCell({
                                        children: [new Paragraph({
                                            children: [new TextRun({
                                                text: 'Artist',
                                                bold: true
                                            })]
                                        })],
                                    }),
                                    new TableCell({
                                        children: [new Paragraph({
                                            children: [new TextRun({
                                                text: artifact.artist,
                                                bold: false
                                            })]
                                        })],
                                    }),
                                ],
                            }),
                            new TableRow({
                                children: [
                                    new TableCell({
                                        children: [new Paragraph({
                                            children: [new TextRun({
                                                text: 'Title',
                                                bold: true
                                            })]
                                        })],
                                    }),
                                    new TableCell({
                                        children: [new Paragraph({
                                            children: [new TextRun({
                                                text: artifact.title,
                                                bold: false
                                            })]
                                        })],
                                    }),
                                ],
                            }),
                            new TableRow({
                                children: [
                                    new TableCell({
                                        children: [new Paragraph({
                                            children: [new TextRun({
                                                text: 'Description',
                                                bold: true
                                            })]
                                        })],
                                    }),
                                    new TableCell({
                                        children: [new Paragraph({
                                            children: [new TextRun({
                                                text: artifact.description,
                                                bold: false
                                            })]
                                        })],
                                    }),
                                ],
                            }),
                            new TableRow({
                                children: [
                                    new TableCell({
                                        children: [new Paragraph({
                                            children: [new TextRun({
                                                text: 'Date',
                                                bold: true
                                            })]
                                        })],
                                    }),
                                    new TableCell({
                                        children: [new Paragraph({
                                            children: [new TextRun({
                                                text: artifact.age,
                                                bold: false
                                            })]
                                        })],
                                    }),
                                ],
                            }),
                            new TableRow({
                                children: [
                                    new TableCell({
                                        children: [new Paragraph({
                                            children: [new TextRun({
                                                text: 'Dimensions',
                                                bold: true
                                            })]
                                        })],
                                    }),
                                    new TableCell({
                                        children: [new Paragraph({
                                            children: [new TextRun({
                                                text: artifact.dimensions,
                                                bold: false
                                            })]
                                        })],
                                    }),
                                ],
                            }),
                            new TableRow({
                                children: [
                                    new TableCell({
                                        children: [new Paragraph({
                                            children: [new TextRun({
                                                text: 'Weight',
                                                bold: true
                                            })]
                                        })],
                                    }),
                                    new TableCell({
                                        children: [new Paragraph({
                                            children: [new TextRun({
                                                text: artifact.weight,
                                                bold: false
                                            })]
                                        })],
                                    }),
                                ],
                            }),
                            new TableRow({
                                children: [
                                    new TableCell({
                                        children: [new Paragraph({
                                            children: [new TextRun({
                                                text: 'Condition',
                                                bold: true
                                            })]
                                        })],
                                    }),
                                    new TableCell({
                                        children: [new Paragraph({
                                            children: [new TextRun({
                                                text: artifact.condition,
                                                bold: false
                                            })]
                                        })],
                                    }),
                                ],
                            }),
                            new TableRow({
                                children: [
                                    new TableCell({
                                        children: [new Paragraph({
                                            children: [new TextRun({
                                                text: 'Origin',
                                                bold: true
                                            })]
                                        })],
                                    }),
                                    new TableCell({
                                        children: [new Paragraph({
                                            children: [new TextRun({
                                                text: artifact.origin,
                                                bold: false
                                            })]
                                        })],
                                    }),
                                ],
                            }),
                            new TableRow({
                                children: [
                                    new TableCell({
                                        children: [new Paragraph({
                                            children: [new TextRun({
                                                text: 'Style',
                                                bold: true
                                            })]
                                        })],
                                    }),
                                    new TableCell({
                                        children: [new Paragraph({
                                            children: [new TextRun({
                                                text: artifact.style,
                                                bold: false
                                            })]
                                        })],
                                    }),
                                ],
                            }),
                            new TableRow({
                                children: [
                                    new TableCell({
                                        children: [new Paragraph({
                                            children: [new TextRun({
                                                text: 'Material',
                                                bold: true
                                            })]
                                        })],
                                    }),
                                    new TableCell({
                                        children: [new Paragraph({
                                            children: [new TextRun({
                                                text: artifact.material,
                                                bold: false
                                            })]
                                        })],
                                    }),
                                ],
                            }),
                            new TableRow({
                                children: [
                                    new TableCell({
                                        children: [new Paragraph({
                                            children: [new TextRun({
                                                text: 'Current Replacement Value for Insurance Inventory Purposes',
                                                bold: true
                                            })]
                                        })],
                                    }),
                                    new TableCell({
                                        children: [new Paragraph({
                                            children: [new TextRun({
                                                text: artifact.estimatedValue,
                                                bold: false
                                            })]
                                        })],
                                    }),
                                ],
                            }),
                        ],
                    }),
                ],
            },
        ],
    });
}

export async function exportMultipleArtifactsDocx(
    artifacts: IArtifactFlat[],
    options: ExportDocxOptions = {}
): Promise<void> {
    const { fileName = `artifacts_export.docx` } = options;

    // 1) Build a new Document
    const doc = await generateMultipleArtifactsDoc(artifacts);

    // 2) Pack to a Blob and trigger download
    const blob = await Packer.toBlob(doc);
    saveAs(blob, fileName);
}

export async function generateMultipleArtifactsDoc(artifacts: IArtifactFlat[]): Promise<any> {
    const TARGET_WIDTH = 400;

    async function getImageDimensionsFromBase64(base64: string): Promise<{ width: number, height: number }> {
        return new Promise((resolve, reject) => {
            const img = new window.Image();
            img.onload = () => resolve({ width: img.width, height: img.height });
            img.onerror = reject;
            img.src = `data:image/png;base64, ${base64}`;
        });
    }

    async function getImageParagraphsForArtifact(artifact: IArtifactFlat): Promise<any[]> {
        const imageData = await Promise.all(
            (artifact.photos || []).map(async (id) => {
                const photoUrl = `/Photo/GetBase64/${id}`;
                try {
                    const res = await _get(photoUrl);
                    if (res && res.data?.base64) {
                        const { base64 } = res.data;
                        const buf = base64ToArrayBuffer(base64);
                        const { width, height } = await getImageDimensionsFromBase64(base64);
                        const scale = TARGET_WIDTH / width;

                        return {
                            buf,
                            width: TARGET_WIDTH,
                            height: Math.round(height * scale),
                        };
                    }
                } catch (err) {
                    console.error(err);
                }
                return null;
            })
        );
        
        return imageData
            .filter((img): img is { buf: ArrayBuffer, width: number, height: number } => !!img)
            .map((img) =>
                new Paragraph({
                    children: [
                        new ImageRun({
                            data: img.buf,
                            type: 'png',
                            transformation: { width: 300, height: 200 }, // Fixed larger size
                        }),
                    ],
                    spacing: { after: 200 }, // Add some spacing between images
                })
            );
    }

    const children: any[] = [
        new Paragraph({
            children: [
                new TextRun({
                    text: '',
                    bold: true,
                    size: 32,
                }),
            ],
        }),
        new Paragraph({}),
    ];

    for (let index = 0; index < artifacts.length; index++) {
        const artifact = artifacts[index];
        // Add a page break before each artifact except the first one
        if (index > 0) {
            children.push(new Paragraph({ pageBreakBefore: true }));
        }
        // Artifact title
        children.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: `${artifact.title}`,
                        bold: true,
                        size: 28,
                    }),
                ],
            })
        );
        // Id
        children.push(
            new Paragraph({
                children: [new TextRun(`artifact_id: ${artifact.id}`)],
            })
        );
        // Images as paragraphs
        // eslint-disable-next-line no-await-in-loop
        const imageParagraphs = await getImageParagraphsForArtifact(artifact);
        children.push(...imageParagraphs);
        // Table with artifact details
        children.push(
            new Table({
                width: {
                    size: 100,
                    type: "pct"
                },
                columnWidths: [4000, 8000],
                rows: [
                    new TableRow({
                        children: [
                            new TableCell({
                                children: [new Paragraph({
                                    children: [new TextRun({
                                        text: 'Artist',
                                        bold: true
                                    })]
                                })],
                            }),
                            new TableCell({
                                children: [new Paragraph({
                                    children: [new TextRun({
                                        text: artifact.artist,
                                        bold: false
                                    })]
                                })],
                            }),
                        ],
                    }),
                    new TableRow({
                        children: [
                            new TableCell({
                                children: [new Paragraph({
                                    children: [new TextRun({
                                        text: 'Title',
                                        bold: true
                                    })]
                                })],
                            }),
                            new TableCell({
                                children: [new Paragraph({
                                    children: [new TextRun({
                                        text: artifact.title,
                                        bold: false
                                    })]
                                })],
                            }),
                        ],
                    }),
                    new TableRow({
                        children: [
                            new TableCell({
                                children: [new Paragraph({
                                    children: [new TextRun({
                                        text: 'Description',
                                        bold: true
                                    })]
                                })],
                            }),
                            new TableCell({
                                children: [new Paragraph({
                                    children: [new TextRun({
                                        text: artifact.description,
                                        bold: false
                                    })]
                                })],
                            }),
                        ],
                    }),
                    new TableRow({
                        children: [
                            new TableCell({
                                children: [new Paragraph({
                                    children: [new TextRun({
                                        text: 'Date',
                                        bold: true
                                    })]
                                })],
                            }),
                            new TableCell({
                                children: [new Paragraph({
                                    children: [new TextRun({
                                        text: artifact.age,
                                        bold: false
                                    })]
                                })],
                            }),
                        ],
                    }),
                    new TableRow({
                        children: [
                            new TableCell({
                                children: [new Paragraph({
                                    children: [new TextRun({
                                        text: 'Dimensions',
                                        bold: true
                                    })]
                                })],
                            }),
                            new TableCell({
                                children: [new Paragraph({
                                    children: [new TextRun({
                                        text: artifact.dimensions,
                                        bold: false
                                    })]
                                })],
                            }),
                        ],
                    }),
                    new TableRow({
                        children: [
                            new TableCell({
                                children: [new Paragraph({
                                    children: [new TextRun({
                                        text: 'Weight',
                                        bold: true
                                    })]
                                })],
                            }),
                            new TableCell({
                                children: [new Paragraph({
                                    children: [new TextRun({
                                        text: artifact.weight,
                                        bold: false
                                    })]
                                })],
                            }),
                        ],
                    }),
                    new TableRow({
                        children: [
                            new TableCell({
                                children: [new Paragraph({
                                    children: [new TextRun({
                                        text: 'Condition',
                                        bold: true
                                    })]
                                })],
                            }),
                            new TableCell({
                                children: [new Paragraph({
                                    children: [new TextRun({
                                        text: artifact.condition,
                                        bold: false
                                    })]
                                })],
                            }),
                        ],
                    }),
                    new TableRow({
                        children: [
                            new TableCell({
                                children: [new Paragraph({
                                    children: [new TextRun({
                                        text: 'Origin',
                                        bold: true
                                    })]
                                })],
                            }),
                            new TableCell({
                                children: [new Paragraph({
                                    children: [new TextRun({
                                        text: artifact.origin,
                                        bold: false
                                    })]
                                })],
                            }),
                        ],
                    }),
                    new TableRow({
                        children: [
                            new TableCell({
                                children: [new Paragraph({
                                    children: [new TextRun({
                                        text: 'Style',
                                        bold: true
                                    })]
                                })],
                            }),
                            new TableCell({
                                children: [new Paragraph({
                                    children: [new TextRun({
                                        text: artifact.style,
                                        bold: false
                                    })]
                                })],
                            }),
                        ],
                    }),
                    new TableRow({
                        children: [
                            new TableCell({
                                children: [new Paragraph({
                                    children: [new TextRun({
                                        text: 'Material',
                                        bold: true
                                    })]
                                })],
                            }),
                            new TableCell({
                                children: [new Paragraph({
                                    children: [new TextRun({
                                        text: artifact.material,
                                        bold: false
                                    })]
                                })],
                            }),
                        ],
                    }),
                    new TableRow({
                        children: [
                            new TableCell({
                                children: [new Paragraph({
                                    children: [new TextRun({
                                        text: 'Current Replacement Value for Insurance Inventory Purposes',
                                        bold: true
                                    })]
                                })],
                            }),
                            new TableCell({
                                children: [new Paragraph({
                                    children: [new TextRun({
                                        text: artifact.estimatedValue,
                                        bold: false
                                    })]
                                })],
                            }),
                        ],
                    }),
                ],
            })
        );
    }

    return new Document({
        sections: [
            {
                properties: {},
                children,
            },
        ],
    });
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
}